/**
 * Recommendations Tab — Sprint 5 · Purchasing Recommendations Engine.
 *
 * Advisory-only surface. Renders per-product recommendation cards, each
 * showing a headline (BUY 20,000 L Clear Diesel), a confidence score,
 * a per-domain rationale checklist (INV_LOW · COST_RISING · …), and
 * evidence-backed plain-English narratives. Adds an Advisory Footer to
 * every card so the "engine advises, Owner decides" contract is visible
 * every time an operator reads a recommendation.
 *
 * Also exposes a Human Intelligence panel where Owner/Admin can post
 * time-boxed notes (e.g. "supplier says next-week rack +$0.03") that
 * feed directly into the rationale of subsequent recommendations.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Lightbulb, Info, WarningCircle, Package, ChatCircleText, X, Plus,
  ArrowUp, ArrowDown, MinusCircle, Trash, Gauge, Pencil, Warehouse, Truck,
  Coffee, Sliders,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import RecommendationList from "@/components/RecommendationList";

const prApi = {
  portfolio:      ()               => api.get("/purchasing-recommendations"),
  vocabulary:     ()               => api.get("/purchasing-recommendations/rationale-codes"),
  signals:        (params)         => api.get("/purchasing-recommendations/signals/list", { params }),
  postSignal:     (payload)        => api.post("/purchasing-recommendations/signals", payload),
  dismissSignal:  (id)             => api.patch(`/purchasing-recommendations/signals/${id}/dismiss`),
  getBands:       ()               => api.get("/purchasing-recommendations/confidence-bands"),
  setBands:       (payload)        => api.put("/purchasing-recommendations/confidence-bands", payload),
  getConstraints: ()               => api.get("/purchasing-recommendations/operational-constraints"),
  setConstraint:  (code, payload)  => api.put(`/purchasing-recommendations/operational-constraints/${code}`, payload),
  resetConstraint:(code)           => api.delete(`/purchasing-recommendations/operational-constraints/${code}`),
  getSuppliers:   ()               => api.get("/purchasing-recommendations/supplier-availability"),
  setSupplier:    (code, payload)  => api.put(`/purchasing-recommendations/supplier-availability/${code}`, payload),
  resetSupplier:  (code)           => api.delete(`/purchasing-recommendations/supplier-availability/${code}`),
};

const PRODUCTS = ["regular", "premium", "clear_diesel", "dyed_diesel", "def"];
const PRODUCT_LABELS = {
  regular:      "Regular Gas",
  premium:      "Premium Gas",
  clear_diesel: "Clear Diesel",
  dyed_diesel:  "Dyed Diesel",
  def:          "DEF",
};

const ACTION_STYLES = {
  BUY:   { chip: "bg-amber-100 text-amber-900 border-amber-300",
           dot:  "bg-amber-500" },
  WATCH: { chip: "bg-sky-100 text-sky-900 border-sky-300",
           dot:  "bg-sky-500" },
  HOLD:  { chip: "bg-slate-100 text-slate-700 border-slate-300",
           dot:  "bg-slate-400" },
};

// DEC-049 · presentation-only vocabulary — engine enums stay BUY/WATCH/HOLD.
// User-facing recommendation actions read as ORDER / WATCH / WAIT.
const ACTION_LABEL = { BUY: "ORDER", WATCH: "WATCH", HOLD: "WAIT" };

const DIRECTION_STYLES = {
  supports_buy: { icon: ArrowUp,     className: "text-amber-700" },
  against_buy:  { icon: ArrowDown,   className: "text-slate-500" },
  context:      { icon: MinusCircle, className: "text-slate-400" },
};

const WEIGHT_STYLES = {
  high:   "font-semibold",
  medium: "font-medium",
  low:    "font-normal",
};

const BAND_STYLES = {
  high:   { chip: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "High" },
  medium: { chip: "bg-amber-100 text-amber-900 border-amber-300",       label: "Medium" },
  low:    { chip: "bg-rose-100 text-rose-800 border-rose-300",          label: "Low" },
};

function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
function fmtNum(v, places = 2) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: places, maximumFractionDigits: places });
}

/* ------------------------------------------------------------------
   Rationale Row
   ------------------------------------------------------------------ */
function RationaleRow({ item }) {
  const style = DIRECTION_STYLES[item.direction] || DIRECTION_STYLES.context;
  const Icon = style.icon;
  return (
    <li className="flex items-start gap-2 py-1.5" data-testid={`pr-rationale-${item.code}`}>
      <Icon size={14} className={`mt-0.5 shrink-0 ${style.className}`} weight="bold" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wide text-slate-500 font-mono">{item.code}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600">
            {item.domain}
          </span>
        </div>
        <p className={`text-sm text-slate-800 mt-0.5 ${WEIGHT_STYLES[item.weight] || ""}`}>
          {item.narrative}
        </p>
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------
   Recommendation Card
   ------------------------------------------------------------------ */
function RecommendationCard({ rec }) {
  const style = ACTION_STYLES[rec.action] || ACTION_STYLES.HOLD;
  const rationale = rec.rationale || [];
  const bySupports = rationale.filter((r) => r.direction === "supports_buy");
  const byAgainst  = rationale.filter((r) => r.direction === "against_buy");
  const context    = rationale.filter((r) => r.direction === "context");

  const orderedRationale = [...bySupports, ...byAgainst, ...context];

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden"
         data-testid={`pr-card-${rec.product_code}`}>
      {/* Headline */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3 flex-wrap">
        <div className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${style.chip}`}
             data-testid={`pr-action-${rec.product_code}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
          {ACTION_LABEL[rec.action] || rec.action}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-slate-900">
            {rec.action === "BUY"
              ? <><span className="tabular-nums">{fmtNum(rec.quantity, 0)}</span> L {rec.product_label}
                  {rec.signals?.capacity_bounded ? <span className="text-xs text-amber-700 font-normal ml-2">(capacity-bounded)</span> : null}</>
              : rec.product_label}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {rec.product_code} · Generated {fmtDT(rec.generated_at)}
          </p>
          {rec.summary ? (
            <p className="text-sm text-slate-700 mt-2 italic" data-testid={`pr-summary-${rec.product_code}`}>
              {rec.summary}
            </p>
          ) : null}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Confidence</div>
          <div className="text-lg font-semibold text-slate-900 tabular-nums"
               data-testid={`pr-confidence-${rec.product_code}`}>{rec.confidence}</div>
          {rec.confidence_band && BAND_STYLES[rec.confidence_band] ? (
            <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full border ${BAND_STYLES[rec.confidence_band].chip}`}
                  data-testid={`pr-confidence-band-${rec.product_code}`}>
              {BAND_STYLES[rec.confidence_band].label}
            </span>
          ) : null}
        </div>
      </div>

      {/* Rationale checklist */}
      <div className="px-5 py-4">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1 font-semibold">Reasons</div>
        <ul className="divide-y divide-slate-100" data-testid={`pr-rationale-list-${rec.product_code}`}>
          {orderedRationale.map((r, i) => <RationaleRow key={`${r.code}-${i}`} item={r} />)}
        </ul>
      </div>

      {/* Signals */}
      <div className="px-5 pb-4">
        <details className="text-xs text-slate-600" data-testid={`pr-signals-toggle-${rec.product_code}`}>
          <summary className="cursor-pointer text-slate-700 hover:text-slate-900 font-medium">
            Underlying signals
          </summary>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 tabular-nums">
            <div><span className="text-slate-500">On hand</span> · {rec.signals.on_hand_litres ? `${fmtNum(rec.signals.on_hand_litres, 0)} L` : "—"}</div>
            <div><span className="text-slate-500">Daily demand</span> · {rec.signals.daily_demand_litres ? `${fmtNum(rec.signals.daily_demand_litres, 0)} L/d` : "—"}</div>
            <div><span className="text-slate-500">Days of cover</span> · {rec.signals.days_of_cover ?? "—"}</div>
            <div><span className="text-slate-500">Target</span> · {rec.signals.target_days_of_cover} days</div>
            <div><span className="text-slate-500">Delivered cost</span> · {rec.signals.current_landed_cost ? `$${rec.signals.current_landed_cost}/L` : "—"}</div>
            <div><span className="text-slate-500">Cost trend</span> · {rec.signals.cost_trend_pct != null ? `${rec.signals.cost_trend_pct}%` : "—"}</div>
            <div><span className="text-slate-500">Supplier rack today</span> · {rec.signals.current_rack ? `$${rec.signals.current_rack}/L` : "—"}</div>
            <div><span className="text-slate-500">Supplier rack vs 30d</span> · {rec.signals.rack_vs_30d_mean_pct != null ? `${rec.signals.rack_vs_30d_mean_pct}%` : "—"}</div>
            <div><span className="text-slate-500">Demand WoW</span> · {rec.signals.demand_wow_pct != null ? `${rec.signals.demand_wow_pct}%` : "—"}</div>
            <div><span className="text-slate-500">Tank capacity</span> · {rec.signals.tank_capacity_litres ? `${fmtNum(rec.signals.tank_capacity_litres, 0)} L` : "—"}</div>
            <div><span className="text-slate-500">Headroom</span> · {rec.signals.headroom_litres != null ? `${fmtNum(rec.signals.headroom_litres, 0)} L` : "—"}</div>
            <div><span className="text-slate-500">Min order</span> · {rec.signals.minimum_order_litres ? `${fmtNum(rec.signals.minimum_order_litres, 0)} L` : "—"}</div>
            <div><span className="text-slate-500">Supplier</span> · {rec.signals.supplier_available ? "Available" : <b className="text-rose-700">Unavailable</b>}</div>
            <div><span className="text-slate-500">Human notes</span> · {rec.signals.human_notes_count}</div>
          </div>
        </details>
      </div>

      {/* Advisory footer */}
      <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-start gap-2">
        <Info size={14} className="text-slate-400 mt-0.5 shrink-0" />
        <p className="text-[11px] text-slate-600" data-testid={`pr-advisory-footer-${rec.product_code}`}>
          {rec.advisory_footer}
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
   Human Intelligence panel
   ------------------------------------------------------------------ */
function HumanIntelPanel({ canWrite, refreshKey, onRefresh }) {
  const [signals, setSignals] = useState([]);
  const [product, setProduct] = useState("");

  const load = useCallback(async () => {
    try {
      const params = product ? { product_code: product } : {};
      const { data } = await prApi.signals(params);
      setSignals(data.items || []);
    } catch (e) { /* silent */ }
  }, [product]);
  useEffect(() => { load(); }, [load, refreshKey]);

  // DEC-050 · signal entry moves to Morning Intelligence Capture. This
  // panel is now a read-only view of *what the engine is currently
  // hearing* — dismiss / post actions live in Morning Capture only.
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="pr-hi-panel">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
        <ChatCircleText size={16} className="text-slate-500" />
        <h3 className="text-sm font-semibold text-slate-900">Human Intelligence</h3>
        <select value={product} onChange={(e) => setProduct(e.target.value)}
                data-testid="pr-hi-product"
                className="text-xs rounded border border-slate-300 px-2 py-1 ml-2">
          <option value="">All products</option>
          {PRODUCTS.map((p) => <option key={p} value={p}>{PRODUCT_LABELS[p]}</option>)}
        </select>
        <span className="ml-auto text-xs text-slate-500" data-testid="pr-hi-count">
          {signals.length} active
        </span>
        {canWrite ? (
          <Link to="/morning-capture" data-testid="pr-hi-manage-link"
                className="text-xs font-semibold px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1">
            <Coffee size={12} weight="fill" /> Manage in Morning Capture
          </Link>
        ) : null}
      </div>
      {signals.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-500" data-testid="pr-hi-empty">
          No active notes. Signals feed the engine after they're captured in the Morning Intelligence workspace.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {signals.map((s) => (
            <li key={s.id} className="px-4 py-3 flex items-start gap-3"
                data-testid={`pr-hi-row-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-slate-800">
                    {PRODUCT_LABELS[s.product_code] || s.product_code}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600">
                    {s.kind === "supplier_constraint" ? "Supplier constraint" : s.kind.replace(/_/g, " ")}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-600">
                    weight · {s.weight}
                  </span>
                  {s.source === "morning_intelligence" ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-800 inline-flex items-center gap-1">
                      <Coffee size={9} weight="fill" /> Morning Capture
                    </span>
                  ) : null}
                </div>
                <p className="text-sm text-slate-800 mt-1">{s.text}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {s.posted_by} · {fmtDT(s.posted_at)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------
   Operational Constraints admin (P0 · Sprint 5 completion)
   ------------------------------------------------------------------ */
function OperationalConstraintsPanel({ canWrite, onChanged }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // {product_code, product_label, tank, min}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await prApi.getConstraints(); setItems(data.items || []); }
    catch (e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (row) => setEditing({
    product_code:  row.product_code,
    product_label: row.product_label,
    tank:          row.tank_capacity_litres,
    min:           row.minimum_order_litres,
    source:        row.source,
  });

  const submit = async () => {
    if (!editing) return;
    const tank = parseFloat(editing.tank);
    const min = parseFloat(editing.min);
    if (!isFinite(tank) || tank <= 0) { toast.error("Tank capacity must be > 0"); return; }
    if (!isFinite(min) || min < 0)    { toast.error("Minimum order must be ≥ 0"); return; }
    if (min > tank) { toast.error("Minimum order cannot exceed tank capacity"); return; }
    setSaving(true);
    try {
      await prApi.setConstraint(editing.product_code, {
        tank_capacity_litres: tank,
        minimum_order_litres: min,
      });
      toast.success(`Operational constraints saved for ${editing.product_label}`);
      setEditing(null);
      load(); onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const resetToDefault = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await prApi.resetConstraint(editing.product_code);
      toast.success(`Reset ${editing.product_label} to Ontario default`);
      setEditing(null);
      load(); onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="pr-constraints-panel">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
        <Warehouse size={16} className="text-slate-500" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">Operational Constraints</h3>
          <p className="text-[11px] text-slate-500">
            Per-product tank capacity + minimum order. The engine bounds recommended quantity to physical
            headroom and flips to WATCH when headroom falls below the minimum order.
          </p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Product</th>
              <th className="px-3 py-2 text-right font-medium">Tank capacity</th>
              <th className="px-3 py-2 text-right font-medium">Minimum order</th>
              <th className="px-3 py-2 text-left font-medium">Source</th>
              <th className="px-3 py-2 text-right"/>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.product_code} className="border-t border-slate-100"
                  data-testid={`pr-constraints-row-${it.product_code}`}>
                <td className="px-3 py-2 font-medium text-slate-800">{it.product_label}</td>
                <td className="px-3 py-2 text-right tabular-nums" data-testid={`pr-constraints-cap-${it.product_code}`}>
                  {Number(it.tank_capacity_litres).toLocaleString()} L
                </td>
                <td className="px-3 py-2 text-right tabular-nums" data-testid={`pr-constraints-min-${it.product_code}`}>
                  {Number(it.minimum_order_litres).toLocaleString()} L
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${it.source === "custom" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                    {it.source}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {canWrite ? (
                    <button onClick={() => openEdit(it)}
                            data-testid={`pr-constraints-edit-${it.product_code}`}
                            className="text-xs text-slate-800 hover:text-slate-950 font-semibold inline-flex items-center gap-1">
                      <Pencil size={12} /> Edit
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing ? (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             data-testid="pr-constraints-dialog">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Warehouse size={16} className="text-slate-500" />
                <h3 className="text-base font-semibold text-slate-900">Constraints · {editing.product_label}</h3>
              </div>
              <button onClick={() => setEditing(null)} data-testid="pr-constraints-close"
                      className="text-slate-500 hover:text-slate-900"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs text-slate-600">Tank capacity (litres)</label>
                <input value={editing.tank} type="number" min="0"
                       onChange={(e) => setEditing({ ...editing, tank: e.target.value })}
                       data-testid="pr-constraints-tank-input"
                       className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5 tabular-nums" />
              </div>
              <div>
                <label className="text-xs text-slate-600">Minimum order (litres)</label>
                <input value={editing.min} type="number" min="0"
                       onChange={(e) => setEditing({ ...editing, min: e.target.value })}
                       data-testid="pr-constraints-min-input"
                       className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5 tabular-nums" />
              </div>
              <p className="text-[11px] text-slate-500">Rule: 0 ≤ minimum ≤ capacity.</p>
            </div>
            <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-200">
              {editing.source === "custom" ? (
                <button onClick={resetToDefault} disabled={saving}
                        data-testid="pr-constraints-reset"
                        className="text-xs px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50">
                  Reset to default
                </button>
              ) : <span/>}
              <div className="flex items-center gap-2">
                <button onClick={() => setEditing(null)} data-testid="pr-constraints-cancel"
                        className="text-sm px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50">Cancel</button>
                <button onClick={submit} disabled={saving} data-testid="pr-constraints-submit"
                        className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}


/* ------------------------------------------------------------------
   Supplier Availability admin (P0 · Sprint 5 completion)
   ------------------------------------------------------------------ */
function SupplierAvailabilityPanel({ canWrite, onChanged }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await prApi.getSuppliers(); setItems(data.items || []); }
    catch (e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (row) => setEditing({
    product_code:  row.product_code,
    product_label: row.product_label,
    available:     row.available,
    note:          row.note || "",
  });

  const submit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await prApi.setSupplier(editing.product_code, {
        available: !!editing.available,
        note:      editing.note || null,
      });
      toast.success(`Supplier availability saved for ${editing.product_label}`);
      setEditing(null);
      load(); onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="pr-supplier-panel">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
        <Truck size={16} className="text-slate-500" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">Supplier Availability</h3>
          <p className="text-[11px] text-slate-500">
            Persistent per-product flag. When a supplier is unavailable, the engine surfaces a
            SUPPLIER_CONSTRAINT rationale on every recommendation until reset.
          </p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Product</th>
              <th className="px-3 py-2 text-left font-medium">Available?</th>
              <th className="px-3 py-2 text-left font-medium">Note</th>
              <th className="px-3 py-2 text-left font-medium">Updated</th>
              <th className="px-3 py-2 text-right"/>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.product_code} className="border-t border-slate-100"
                  data-testid={`pr-supplier-row-${it.product_code}`}>
                <td className="px-3 py-2 font-medium text-slate-800">{it.product_label}</td>
                <td className="px-3 py-2">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${it.available ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-rose-50 text-rose-800 border-rose-200"}`}
                        data-testid={`pr-supplier-flag-${it.product_code}`}>
                    {it.available ? "Available" : "Unavailable"}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600 max-w-md truncate" title={it.note || ""}>
                  {it.note || <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2 text-[11px] text-slate-500">
                  {it.updated_by ? <>{it.updated_by} · {fmtDT(it.updated_at)}</> : <span className="text-slate-400">never</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  {canWrite ? (
                    <button onClick={() => openEdit(it)}
                            data-testid={`pr-supplier-edit-${it.product_code}`}
                            className="text-xs text-slate-800 hover:text-slate-950 font-semibold inline-flex items-center gap-1">
                      <Pencil size={12} /> Edit
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing ? (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             data-testid="pr-supplier-dialog">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Truck size={16} className="text-slate-500" />
                <h3 className="text-base font-semibold text-slate-900">Supplier · {editing.product_label}</h3>
              </div>
              <button onClick={() => setEditing(null)} data-testid="pr-supplier-close"
                      className="text-slate-500 hover:text-slate-900"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3">
                <label className="text-xs text-slate-600">Available?</label>
                <select value={editing.available ? "yes" : "no"}
                        onChange={(e) => setEditing({ ...editing, available: e.target.value === "yes" })}
                        data-testid="pr-supplier-available"
                        className="text-sm rounded border border-slate-300 px-2 py-1.5">
                  <option value="yes">Available</option>
                  <option value="no">Unavailable</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-600">Note (optional · shown on every recommendation)</label>
                <textarea value={editing.note}
                          onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                          data-testid="pr-supplier-note"
                          rows={3}
                          placeholder="e.g. Supplier maintenance shutdown · reopens Monday"
                          className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
              <button onClick={() => setEditing(null)} data-testid="pr-supplier-cancel"
                      className="text-sm px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={submit} disabled={saving} data-testid="pr-supplier-submit"
                      className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}


/* ------------------------------------------------------------------
   Confidence Bands admin (P1) — scaffold; values deferred until PO
   configures thresholds after 2-4 weeks of real signal data.
   ------------------------------------------------------------------ */
function ConfidenceBandsPanel({ canWrite, onChanged }) {
  const [state, setState] = useState(null);
  const [editing, setEditing] = useState(false);
  const [highMin, setHighMin] = useState("");
  const [medMin, setMedMin] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await prApi.getBands(); setState(data); }
    catch (e) { /* silent · non-critical */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = () => {
    setHighMin(state?.high_min != null ? String(state.high_min) : "");
    setMedMin(state?.medium_min != null ? String(state.medium_min) : "");
    setEditing(true);
  };

  const submit = async (clear = false) => {
    setSaving(true);
    try {
      const payload = clear
        ? { high_min: null, medium_min: null }
        : { high_min: parseInt(highMin, 10), medium_min: parseInt(medMin, 10) };
      if (!clear) {
        if (!isFinite(payload.high_min) || !isFinite(payload.medium_min)) {
          toast.error("Both thresholds are required (0–100)"); setSaving(false); return;
        }
        if (!(0 <= payload.medium_min && payload.medium_min <= payload.high_min && payload.high_min <= 100)) {
          toast.error("Require 0 ≤ Medium ≤ High ≤ 100"); setSaving(false); return;
        }
      }
      await prApi.setBands(payload);
      toast.success(clear ? "Bands cleared — engine reverted to numeric only" : "Confidence bands saved");
      setEditing(false);
      load(); onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save bands");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="pr-bands-panel">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
        <Gauge size={16} className="text-slate-500" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">Confidence Bands</h3>
          <p className="text-[11px] text-slate-500">
            Map the engine's 0-100 confidence score to High / Medium / Low. Deferred by the PO
            until real signal data accumulates — until you set values here, the engine returns
            numeric confidence only and no band label appears.
          </p>
        </div>
        {canWrite ? (
          <button onClick={openEdit} data-testid="pr-bands-edit"
                  className="text-xs font-semibold px-3 py-1.5 rounded border border-slate-300 text-slate-800 hover:bg-slate-50 inline-flex items-center gap-1">
            <Pencil size={12} /> {state?.configured ? "Edit" : "Configure"}
          </button>
        ) : null}
      </div>
      <div className="px-4 py-3">
        {!state ? <p className="text-sm text-slate-500">Loading…</p> :
         !state.configured ? (
          <p className="text-sm text-slate-600" data-testid="pr-bands-unconfigured">
            Not yet configured. Numeric confidence is shown without labels.
          </p>
        ) : (
          <div className="flex items-center gap-3 text-xs" data-testid="pr-bands-configured">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border ${BAND_STYLES.high.chip}`}>
              High ≥ <b className="ml-1 tabular-nums">{state.high_min}</b>
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border ${BAND_STYLES.medium.chip}`}>
              Medium ≥ <b className="ml-1 tabular-nums">{state.medium_min}</b>
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border ${BAND_STYLES.low.chip}`}>
              Low &lt; <b className="ml-1 tabular-nums">{state.medium_min}</b>
            </span>
            <span className="ml-auto text-slate-500">
              Set by {state.updated_by} · {fmtDT(state.updated_at)}
            </span>
          </div>
        )}
      </div>

      {editing ? (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             data-testid="pr-bands-dialog">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Gauge size={16} className="text-slate-500" />
                <h3 className="text-base font-semibold text-slate-900">Confidence Bands</h3>
              </div>
              <button onClick={() => setEditing(false)} data-testid="pr-bands-close"
                      className="text-slate-500 hover:text-slate-900"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-slate-600">
                The engine emits a 0–100 confidence score for each recommendation.
                Set the two thresholds below; the engine will label anything ≥ High as
                "High", ≥ Medium as "Medium", and below Medium as "Low". Both fields must
                be set — or both cleared to disable band labels.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-600">High ≥</label>
                  <input value={highMin} onChange={(e) => setHighMin(e.target.value)}
                         data-testid="pr-bands-high-input"
                         type="number" min="0" max="100"
                         className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5 tabular-nums" />
                </div>
                <div>
                  <label className="text-xs text-slate-600">Medium ≥</label>
                  <input value={medMin} onChange={(e) => setMedMin(e.target.value)}
                         data-testid="pr-bands-medium-input"
                         type="number" min="0" max="100"
                         className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5 tabular-nums" />
                </div>
              </div>
              <p className="text-[11px] text-slate-500">
                Rule: 0 ≤ Medium ≤ High ≤ 100.
              </p>
            </div>
            <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-200">
              {state?.configured ? (
                <button onClick={() => submit(true)} disabled={saving} data-testid="pr-bands-clear"
                        className="text-xs px-3 py-1.5 rounded border border-slate-300 text-rose-700 hover:bg-rose-50">
                  Clear bands
                </button>
              ) : <span/>}
              <div className="flex items-center gap-2">
                <button onClick={() => setEditing(false)} data-testid="pr-bands-cancel"
                        className="text-sm px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50">Cancel</button>
                <button onClick={() => submit(false)} disabled={saving} data-testid="pr-bands-submit"
                        className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------
   Root
   ------------------------------------------------------------------ */
export default function RecommendationsTab({ canWrite }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await prApi.portfolio(); setData(data); }
    catch (e) { toast.error("Failed to load recommendations"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const summary = useMemo(() => {
    if (!data) return { BUY: 0, WATCH: 0, HOLD: 0 };
    return (data.items || []).reduce((acc, it) => {
      acc[it.action] = (acc[it.action] || 0) + 1;
      return acc;
    }, { BUY: 0, WATCH: 0, HOLD: 0 });
  }, [data]);

  return (
    <div className="space-y-5" data-testid="pr-recommendations-tab">
      {/* Unified Recommendation Engine · Sprint 3C · the same feed
          rendered on the Morning Brief and (Sprint 3D) the OCC card. */}
      <RecommendationList
        title="Today's Recommendations"
        testidPrefix="pr-recs"
      />

      <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4 flex items-start gap-3">
        <Lightbulb size={18} className="text-amber-600 mt-0.5 shrink-0" weight="fill" />
        <div>
          <p className="text-sm font-semibold text-amber-900">Advisory Recommendations</p>
          <p className="text-xs text-amber-800 mt-0.5">
            {data?.advisory_notice ||
              "This engine assembles inventory, cost, market, demand, and human-intelligence signals into a suggested action. The Owner still decides."}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-3 text-xs">
          <div className="text-slate-600" data-testid="pr-summary-buy">
            <b className="text-amber-700">{summary.BUY}</b> ORDER
          </div>
          <div className="text-slate-600" data-testid="pr-summary-watch">
            <b className="text-sky-700">{summary.WATCH}</b> WATCH
          </div>
          <div className="text-slate-600" data-testid="pr-summary-hold">
            <b className="text-slate-700">{summary.HOLD}</b> WAIT
          </div>
        </div>
      </div>

      <details className="rounded-lg border border-slate-200 bg-white overflow-hidden group"
               data-testid="pr-engine-config">
        <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 flex items-center gap-2">
          <Gauge size={16} className="text-slate-500" /> Engine Configuration
          <span className="text-[11px] text-slate-500 font-normal ml-2">
            Operational constraints · Supplier availability
          </span>
          <Link to="/admin/thresholds" data-testid="pr-thresholds-link"
                className="ml-auto text-[11px] font-semibold px-2 py-1 rounded bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}>
            <Sliders size={11} weight="fill" /> Threshold Administration
          </Link>
        </summary>
        <div className="p-4 space-y-3 border-t border-slate-100">
          <OperationalConstraintsPanel canWrite={canWrite} onChanged={() => setRefreshKey((k) => k + 1)} />
          <SupplierAvailabilityPanel canWrite={canWrite} onChanged={() => setRefreshKey((k) => k + 1)} />
          {/* DEC-051 · Confidence Bands moved to Threshold Administration */}
        </div>
      </details>

      <HumanIntelPanel canWrite={canWrite} refreshKey={refreshKey}
                        onRefresh={() => setRefreshKey((k) => k + 1)} />

      {loading && !data ? (
        <p className="text-sm text-slate-500">Loading recommendations…</p>
      ) : !data || (data.items || []).length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
          No products active.
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="pr-cards-grid">
          {data.items.map((rec) => <RecommendationCard key={rec.product_code} rec={rec} />)}
        </div>
      )}
    </div>
  );
}
